// Workspace sub-nav shell. Renders the 5 top-level tabs and hands off
// to a nested `<Outlet />` for the picked screen.
//
// Wave N MVP: no per-role hiding on Team Overwatch / Admin — the backend
// still enforces permissions; we just fade the disabled entries visually
// so managers see what's available if they gain the role later.
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import { Inbox, Settings2, Kanban, Network, Search, Contact } from "lucide-react";
import { isMgmt } from "./_shared";
import { useAuth } from "../../context/AuthContext";
import OverwatchButton from "./panels/OverwatchButton";

const TABS = [
  { to: "/workspace/my-tasks",  label: "Tasks",       icon: Inbox,           testid: "ws-tab-tasks", also: ["/workspace/tasks"] },
  { to: "/workspace/crm",       label: "My CRM",      icon: Contact,         testid: "ws-tab-crm" },
  { to: "/workspace/org-chart", label: "People",      icon: Network,         testid: "ws-tab-people", also: ["/workspace/full-squad"] },
  { to: "/workspace/admin",     label: "Admin",       icon: Settings2,       testid: "ws-tab-admin", adminOnly: true, also: ["/workspace/audit"] },
];
const PEOPLE = [
  { to: "/workspace/org-chart",  label: "Org chart",  testid: "ws-people-org" },
  { to: "/workspace/full-squad", label: "Full squad", testid: "ws-people-squad" },
];

export default function WorkspaceLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const onTasks = ["/workspace/my-tasks", "/workspace/tasks"].some((p) => pathname.startsWith(p));
  const isAdmin   = !!(user?.system_admin || user?.is_admin
    || ["admin", "owner", "system_admin"].includes((user?.role || "").toLowerCase()));
  const isManager = isMgmt(user);
  return (
    <Layout>
      <div className="mb-4 flex items-center gap-3" data-testid="workspace-header">
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/40 grid place-items-center">
          <Kanban className="w-4 h-4 text-amber-300" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono">Talmia 2</div>
          <h1 className="text-xl font-bold text-white">Workspace</h1>
        </div>
        <OverwatchButton />
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6 border-b border-white/[0.06]">
      <nav
        className="flex items-center gap-1 overflow-x-auto"
        data-testid="workspace-subnav"
      >
        {TABS.map((t) => {
          const gated = (t.adminOnly && !isAdmin) || (t.managerOnly && !isManager);
          if (gated) return null;
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive: hit }) => { const isActive = hit || (t.also || []).some((p) => pathname.startsWith(p)); return (
                `inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                  isActive
                    ? "border-amber-400 text-amber-200"
                    : "border-transparent text-slate-400 hover:text-white"
                }`); }
              }
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </NavLink>
          );
        })}
      </nav>
      {onTasks && (
        <div className="relative ml-auto w-full sm:w-[340px] mb-1.5">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input className="ws-input !pl-9 !rounded-full" placeholder="Search tasks — title, ref, tags…" value={params.get("q") || ""}
            onChange={(e) => { const p = new URLSearchParams(params); e.target.value ? p.set("q", e.target.value) : p.delete("q"); setParams(p, { replace: true }); }}
            data-testid="task-search" />
        </div>
      )}
      </div>
      {PEOPLE.some((p) => pathname.startsWith(p.to)) && (
        <div className="flex gap-1.5 -mt-3 mb-5" data-testid="ws-people-subnav">
          {PEOPLE.map((p) => (
            <NavLink key={p.to} to={p.to} data-testid={p.testid}
              className={({ isActive }) => `px-3 py-1 rounded-full text-[12px] border transition-colors ${isActive ? "border-amber-400/50 bg-amber-500/10 text-amber-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {p.label}
            </NavLink>
          ))}
        </div>
      )}

      <Outlet />
    </Layout>
  );
}
